import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';

/**
 * ConfirmProvider / useConfirm — imperative, promise-based confirmation,
 * a styled replacement for window.confirm() (UX §8: feedback in the interface,
 * not a native browser dialog).
 *
 * Wrap the app once:  <ConfirmProvider><App /></ConfirmProvider>
 * Then anywhere:
 *   const confirm = useConfirm();
 *   if (!(await confirm('Delete this?'))) return;                       // string form
 *   if (!(await confirm({ title: 'Delete coupon?', message: '…',        // options form
 *                         confirmLabel: 'Delete', danger: true }))) return;
 *
 * Resolves true on confirm, false on cancel. Reuses the styled <ConfirmDialog>.
 * Fails soft to window.confirm if the provider is missing, so a destructive-action
 * guard never breaks. No colour-theme change — ConfirmDialog uses theme tokens.
 */

const ConfirmCtx = createContext(null);

const normalise = (opts) => (typeof opts === 'string' ? { message: opts } : (opts || {}));

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) {
    // Fail soft: fall back to the native dialog so a missing provider never
    // breaks a destructive-action guard.
    return (opts) => Promise.resolve(window.confirm(normalise(opts).message || 'Are you sure?'));
  }
  return ctx;
}

export function ConfirmProvider({ children }) {
  const [req, setReq] = useState(null); // { opts }
  const resolver = useRef(null);

  const confirm = useCallback((opts) => new Promise((resolve) => {
    resolver.current = resolve;
    setReq({ opts: normalise(opts) });
  }), []);

  const settle = useCallback((result) => {
    const r = resolver.current;
    resolver.current = null;
    setReq(null);
    r?.(result);
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmCtx.Provider value={value}>
      {children}
      {req && (
        <ConfirmDialog
          title={req.opts.title || 'Are you sure?'}
          message={req.opts.message}
          confirmLabel={req.opts.confirmLabel || 'Confirm'}
          cancelLabel={req.opts.cancelLabel || 'Cancel'}
          danger={req.opts.danger !== false}
          onConfirm={() => settle(true)}
          onCancel={() => settle(false)}
        />
      )}
    </ConfirmCtx.Provider>
  );
}
